import { Alert, Flex, Pill, Text } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import RichTextEditor from "react-rte";
import { formatToReadableTime } from "../../../utils/utils";
import { Card } from "../../Card";
import CustomRTE, { EMPTY_RTE } from "../../CustomTextArea/CustomRTE";
import HelperHeading from "../HelperHeading";

const ReflectionsAndActionsCard = ({
  reflections,
  reflectionsSummary,
  actions,
  actionsSummary,
  stats,
}) => {
  const { t } = useTranslation();

  return (
    <Card style={{ height: "auto", marginTop: "1.5rem" }}>
      <div style={{ display: "flex", gap: "1.5rem" }}>
        <div style={{ flex: 1 }}>
          <HelperHeading
            title={t("reflections.courseReviewReflections")}
            stats={
              stats.section_count > 0
                ? t("courseReview.sectionsWithReflections", {
                    num: stats.published_reflections_count,
                    total: stats.section_count,
                  })
                : ""
            }
          />
          <Flex gap="medium" wrap="wrap">
            {reflectionsSummary !== EMPTY_RTE && (
              <Flex.Item>
                <CustomRTE
                  id="reflection-summary"
                  label={
                    <Text as="p" weight="bold">
                      {t("courseReview.summary")}
                    </Text>
                  }
                  defaultValue={reflectionsSummary}
                  readOnly
                />
              </Flex.Item>
            )}
            {reflections.length > 0 ? (
              reflections.map((ref, key) => (
                <Flex.Item key={key} size="100%">
                  <Card style={{ border: "1px solid #ccc", padding: "10px" }}>
                    <Flex
                      alignItems="start"
                      gap="small"
                      margin="0 0 small 0"
                      wrap="wrap"
                    >
                      <Flex.Item shouldShrink shouldGrow>
                        <Text as="p" fontStyle="italic" size="small">
                          {ref.metadata.author}
                          {" - "}
                          {formatToReadableTime(new Date(ref.timestamp))}
                        </Text>
                      </Flex.Item>
                    </Flex>
                    {ref.from_template ? (
                      <Flex wrap="wrap" gap="medium">
                        {ref.sections.map((section) => (
                          <Flex.Item key={section.id} width="100%">
                            <Flex wrap="wrap" gap="small">
                              <Flex.Item width="100%">
                                <Text weight="bold">
                                  {section.header_question}
                                </Text>
                              </Flex.Item>
                              <Flex.Item>
                                <div
                                  style={{
                                    borderLeft: "2px solid #03893D",
                                    paddingLeft: "0.75rem",
                                  }}
                                >
                                  <RichTextEditor
                                    className="readOnlyClass"
                                    value={RichTextEditor.createValueFromString(
                                      section.content,
                                      "html"
                                    )}
                                    readOnly
                                  />
                                </div>
                              </Flex.Item>
                            </Flex>
                          </Flex.Item>
                        ))}
                      </Flex>
                    ) : (
                      <RichTextEditor
                        className="readOnlyClass"
                        value={RichTextEditor.createValueFromString(
                          ref.content,
                          "html"
                        )}
                        readOnly
                      />
                    )}
                  </Card>
                </Flex.Item>
              ))
            ) : (
              <Flex.Item size="100%">
                <Alert>{t("courseReview.noReflections")}</Alert>
              </Flex.Item>
            )}
          </Flex>
        </div>
        <div style={{ flex: 1 }}>
          <HelperHeading
            title={t("actions.courseReviewActions")}
            stats={
              stats.section_count > 0
                ? t("courseReview.sectionsWithActions", {
                    num: stats.actions_count,
                    total: stats.section_count,
                  })
                : ""
            }
          />
          <Flex gap="medium" wrap="wrap">
            {actionsSummary !== EMPTY_RTE && (
              <Flex.Item>
                <CustomRTE
                  id="action-summary"
                  label={
                    <Text as="p" weight="bold">
                      {t("courseReview.summary")}
                    </Text>
                  }
                  defaultValue={actionsSummary}
                  readOnly
                />
              </Flex.Item>
            )}
            {actions.length > 0 ? (
              actions.map((action, key) => (
                <Flex.Item key={key} size="100%">
                  <Card style={{ border: "1px solid #ccc", padding: "10px" }}>
                    <Flex alignItems="start" gap="small" wrap="wrap">
                      <Flex.Item shouldShrink shouldGrow>
                        <Text as="p" fontStyle="italic" size="small">
                          {action.author}
                          {" - "}
                          {formatToReadableTime(
                            new Date(action.timestamp + "Z")
                          )}
                        </Text>
                      </Flex.Item>
                      <Flex.Item>
                        <Pill color="info">{action.action_type.name}</Pill>
                      </Flex.Item>
                      <Flex.Item size="100%">
                        <Text as="p" size="small">
                          {action.text}
                        </Text>
                      </Flex.Item>
                    </Flex>
                  </Card>
                </Flex.Item>
              ))
            ) : (
              <Flex.Item size="100%">
                <Alert>{t("courseReview.noActions")}</Alert>
              </Flex.Item>
            )}
          </Flex>
        </div>
      </div>
    </Card>
  );
};

export default ReflectionsAndActionsCard;
